import { useCallback, useEffect, useState } from 'react';
import { Gift, History, Save, Settings, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getLoyaltySettings,
  listAllRewards,
  listLoyaltyCustomers,
  updateLoyaltySettings
} from '../api/loyalty';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import CustomerLoyaltyModal from '../components/CustomerLoyaltyModal';
import FormField, { inputClassName } from '../components/FormField';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import { date, dateTime } from '../utils/format';

const rewardStatus = {
  active: { label: 'Activo', color: 'green' },
  redeemed: { label: 'Canjeado', color: 'blue' },
  expired: { label: 'Expirado', color: 'slate' }
};

const statusForReward = (reward) => {
  if (reward.redeemed_at) return rewardStatus.redeemed;
  if (reward.expires_at && new Date(reward.expires_at) < new Date()) return rewardStatus.expired;
  return rewardStatus.active;
};

const Fidelizacion = () => {
  const [customers, setCustomers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [settings, setSettings] = useState({ paws_required: 14, reward_expiry_months: 6, allow_manual_adjustments: 1 });
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [customerData, rewardData, settingsData] = await Promise.all([
        listLoyaltyCustomers({ limit: 100 }),
        listAllRewards({ limit: 100 }),
        getLoyaltySettings()
      ]);
      setCustomers(customerData);
      setRewards(rewardData);
      setSettings({
        paws_required: settingsData?.paws_required ?? 14,
        reward_expiry_months: settingsData?.reward_expiry_months ?? 6,
        allow_manual_adjustments: settingsData?.allow_manual_adjustments ?? 1
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar fidelización'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveSettings = async (event) => {
    event.preventDefault();

    try {
      setSavingSettings(true);
      await updateLoyaltySettings({
        paws_required: Number(settings.paws_required),
        reward_expiry_months: Number(settings.reward_expiry_months),
        allow_manual_adjustments: settings.allow_manual_adjustments ? 1 : 0
      });
      toast.success('Reglas de fidelización actualizadas');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar fidelización'));
    } finally {
      setSavingSettings(false);
    }
  };

  const customerColumns = [
    { key: 'full_name', label: 'Cliente' },
    { key: 'email', label: 'Email', render: (row) => row.email || '-' },
    { key: 'phone', label: 'Telefono', render: (row) => row.phone || '-' },
    { key: 'created_at', label: 'Registro', render: (row) => date(row.created_at) },
    {
      key: 'current_paws',
      label: 'Patitas',
      render: (row) => `${row.current_paws ?? 0} / ${settings.paws_required || 14}`
    },
    {
      key: 'rewards_available',
      label: 'Premios activos',
      render: (row) => row.rewards_available ?? 0
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <button
          type="button"
          onClick={() => setSelectedCustomer(row)}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          title="Ver historial de patitas"
        >
          <History className="h-4 w-4" />
          Movimientos
        </button>
      )
    }
  ];

  const rewardColumns = [
    { key: 'id', label: 'Premio', render: (row) => `#${row.id}` },
    { key: 'customer_id', label: 'Cliente', render: (row) => `#${row.customer_id}` },
    { key: 'name', label: 'Nombre' },
    { key: 'reward_type', label: 'Tipo' },
    { key: 'earned_at', label: 'Obtenido', render: (row) => dateTime(row.earned_at) },
    { key: 'expires_at', label: 'Expira', render: (row) => date(row.expires_at) },
    { key: 'redeemed_at', label: 'Canjeado', render: (row) => dateTime(row.redeemed_at) },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const status = statusForReward(row);
        return <StatusBadge label={status.label} color={status.color} />;
      }
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fidelizacion"
        description="Reglas de patitas, premios, canjes, expiración e historial por cliente."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard title="Clientes visibles" value={customers.length} icon={Users} color="blue" loading={loading} />
        <StatCard title="Premios creados" value={rewards.length} icon={Gift} color="purple" loading={loading} />
        <StatCard title="Patitas por premio" value={settings.paws_required || 14} icon={Settings} color="green" loading={loading} />
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <form onSubmit={saveSettings} className="grid gap-3 md:grid-cols-4">
          <FormField label="Patitas requeridas">
            <input
              type="number"
              min="1"
              className={inputClassName}
              value={settings.paws_required}
              onChange={(event) => setSettings({ ...settings, paws_required: event.target.value })}
            />
          </FormField>
          <FormField label="Expiración meses">
            <input
              type="number"
              min="1"
              className={inputClassName}
              value={settings.reward_expiry_months}
              onChange={(event) => setSettings({ ...settings, reward_expiry_months: event.target.value })}
            />
          </FormField>
          <div className="flex items-end">
            <label className="flex h-11 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(settings.allow_manual_adjustments)}
                onChange={(event) => setSettings({ ...settings, allow_manual_adjustments: event.target.checked ? 1 : 0 })}
              />
              Ajustes manuales
            </label>
          </div>
          <div className="flex items-end">
            <Button type="submit" icon={Save} disabled={savingSettings}>
              Guardar reglas
            </Button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="p-5"><LoadingState rows={5} /></div>
        ) : (
          <Table columns={customerColumns} data={customers} emptyMessage="No hay clientes registrados" />
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-950">Premios y canjes</h2>
        </div>
        {loading ? (
          <div className="p-5"><LoadingState rows={5} /></div>
        ) : (
          <Table columns={rewardColumns} data={rewards} emptyMessage="No hay premios registrados" />
        )}
      </section>

      <CustomerLoyaltyModal
        customer={selectedCustomer}
        open={Boolean(selectedCustomer)}
        onClose={() => setSelectedCustomer(null)}
      />
    </div>
  );
};

export default Fidelizacion;
